/**
 * EchoPaste Protocol Foundation (Phase 0)
 *
 * Defines shared wire types, constants, and envelopes for EchoPaste clients and edge worker.
 */

export const PROTOCOL_VERSION = 1 as const;

export type ClipType = "text" | "url";

export const MEDIA_MAX_PLAINTEXT_BYTES = 25 * 1024 * 1024;
export const MEDIA_CHUNK_BYTES = 4 * 1024 * 1024;
export const NOTEBOOK_TEXT_MAX_BYTES = 64 * 1024;
export type MediaMode = "room_broadcast" | "notebook_delivery";
export type MediaKind = "text" | "image" | "file";
export type MediaState = "uploading" | "available" | "queued" | "claimed" | "imported" | "failed" | "expired" | "deleting";

export interface MediaManifest {
  version: 1;
  kind: MediaKind;
  name?: string;
  mime?: string;
  size: number;
  sha256: string;
  text?: string;
}

export interface MediaChunkDescriptor {
  index: number;
  ciphertextBytes: number;
  ciphertextSha256: string;
  nonce: string;
}

export interface MediaCreateRequest {
  id: string;
  mode: MediaMode;
  contentClass?: "text" | "media";
  recipientDeviceId?: string;
  keyVersion: number;
  totalCiphertextBytes: number;
  chunks: MediaChunkDescriptor[];
  encryptedManifest: string;
  manifestNonce: string;
  wrappedContentKey: string;
  wrappedContentKeyNonce: string;
}

export interface MediaItem extends MediaCreateRequest {
  originDeviceId: string;
  state: MediaState;
  createdAt: string;
  expiresAt: string;
  claimExpiresAt?: string;
  failureCode?: string;
}

const MEDIA_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MEDIA_SHA256 = /^[A-Za-z0-9_-]{43}$/;
const MEDIA_NONCE = /^[A-Za-z0-9_-]{16}$/;

export function isMediaManifest(value: unknown): value is MediaManifest {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return v.version === 1 && ["text", "image", "file"].includes(String(v.kind)) &&
    typeof v.size === "number" && Number.isSafeInteger(v.size) && v.size >= 0 && v.size <= MEDIA_MAX_PLAINTEXT_BYTES &&
    typeof v.sha256 === "string" && MEDIA_SHA256.test(v.sha256) &&
    (v.name === undefined || typeof v.name === "string") && (v.mime === undefined || typeof v.mime === "string") &&
    (v.text === undefined || typeof v.text === "string");
}

export function isMediaCreateRequest(value: unknown): value is MediaCreateRequest {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  if (typeof v.id !== "string" || !MEDIA_ID.test(v.id) || (v.mode !== "room_broadcast" && v.mode !== "notebook_delivery")) return false;
  if (v.mode === "notebook_delivery" && !isNonEmptyString(v.recipientDeviceId)) return false;
  if (v.mode === "room_broadcast" && v.recipientDeviceId !== undefined) return false;
  if (typeof v.keyVersion !== "number" || !Number.isSafeInteger(v.keyVersion) || v.keyVersion < 1) return false;
  if (typeof v.totalCiphertextBytes !== "number" || !Number.isSafeInteger(v.totalCiphertextBytes) || v.totalCiphertextBytes < 0 || v.totalCiphertextBytes > MEDIA_MAX_PLAINTEXT_BYTES + 16 * Math.ceil(MEDIA_MAX_PLAINTEXT_BYTES / MEDIA_CHUNK_BYTES)) return false;
  if (!Array.isArray(v.chunks) || v.chunks.length > Math.ceil(MEDIA_MAX_PLAINTEXT_BYTES / MEDIA_CHUNK_BYTES)) return false;
  const inferredClass = v.mode === "notebook_delivery" && v.totalCiphertextBytes === 0 && v.chunks.length === 0 ? "text" : "media";
  if (v.contentClass !== undefined && v.contentClass !== inferredClass) return false;
  const inlineNotebookText = inferredClass === "text";
  if (!inlineNotebookText && (v.totalCiphertextBytes < 16 || v.chunks.length < 1)) return false;
  if (!v.chunks.every((chunk, index) => {
    if (!chunk || typeof chunk !== "object") return false;
    const c = chunk as Record<string, unknown>;
    return c.index === index && typeof c.ciphertextBytes === "number" && Number.isSafeInteger(c.ciphertextBytes) && c.ciphertextBytes >= 16 && c.ciphertextBytes <= MEDIA_CHUNK_BYTES + 16 && typeof c.ciphertextSha256 === "string" && MEDIA_SHA256.test(c.ciphertextSha256) && typeof c.nonce === "string" && MEDIA_NONCE.test(c.nonce);
  })) return false;
  if ((v.chunks as MediaChunkDescriptor[]).reduce((sum, chunk) => sum + chunk.ciphertextBytes, 0) !== v.totalCiphertextBytes) return false;
  return isNonEmptyString(v.encryptedManifest) && typeof v.manifestNonce === "string" && MEDIA_NONCE.test(v.manifestNonce) &&
    isNonEmptyString(v.wrappedContentKey) && typeof v.wrappedContentKeyNonce === "string" && MEDIA_NONCE.test(v.wrappedContentKeyNonce);
}

export interface ClipEnvelope {
  id: string;
  originDeviceId: string;
  type: ClipType;

  ciphertext: string;
  nonce: string;

  /**
   * Optional content fingerprint for duplicate detection and sync loop prevention.
   * NOTE: Server must NOT rely on plaintext raw SHA-256 for sensitive queries.
   */
  contentHash?: string;

  createdAt: string;
  expiresAt: string;

  version: number;
  keyVersion?: number;
}

export type Platform = "windows" | "android" | "web" | "ios-pwa";

export interface Device {
  id: string;
  name: string;
  platform: Platform;
  createdAt: string;
}

export const HTTP_HEADER_ROOM = "X-EchoPaste-Room" as const;
export const HTTP_HEADER_DEVICE = "X-EchoPaste-Device" as const;
export const HTTP_HEADER_AUTH_VERSION = "X-EchoPaste-Auth-Version" as const;
export const HTTP_HEADER_TIMESTAMP = "X-EchoPaste-Timestamp" as const;
export const HTTP_HEADER_NONCE = "X-EchoPaste-Nonce" as const;
export const HTTP_HEADER_BODY_SHA256 = "X-EchoPaste-Body-SHA256" as const;
export const HTTP_HEADER_SIGNATURE = "X-EchoPaste-Signature" as const;
export const PRODUCTION_DEVICE_AUTH_VERSION = 1 as const;

export interface DeviceAuthFields {
  version: typeof PRODUCTION_DEVICE_AUTH_VERSION;
  roomId: string;
  deviceId: string;
  timestamp: number;
  nonce: string;
  bodySha256: string;
  signature: string;
}

/** WebSocket transports the same proof in its query because browsers cannot set upgrade headers. */
export interface WebSocketAuthProof extends DeviceAuthFields {}

const BASE64URL_256 = /^[A-Za-z0-9_-]{43}$/;
const P256_ECDSA_SIGNATURE = /^[A-Za-z0-9_-]{86}$/;

/** Structural validation only; timestamp-window and signature verification belong to the edge verifier. */
export function isValidDeviceAuthFields(value: unknown): value is DeviceAuthFields {
  if (!value || typeof value !== "object") return false;
  const fields = value as Record<string, unknown>;
  return fields.version === PRODUCTION_DEVICE_AUTH_VERSION &&
    isNonEmptyString(fields.roomId) && isNonEmptyString(fields.deviceId) &&
    typeof fields.timestamp === "number" && Number.isSafeInteger(fields.timestamp) && fields.timestamp >= 0 &&
    typeof fields.nonce === "string" && BASE64URL_256.test(fields.nonce) &&
    typeof fields.bodySha256 === "string" && BASE64URL_256.test(fields.bodySha256) &&
    typeof fields.signature === "string" && P256_ECDSA_SIGNATURE.test(fields.signature);
}

export function isValidWebSocketAuthProof(value: unknown): value is WebSocketAuthProof {
  return isValidDeviceAuthFields(value);
}

export const PAIRING_TOKEN_PREFIX = "EP1" as const;
export const PRODUCTION_INVITATION_PREFIX = "EP2" as const;

/**
 * Creates a versioned development pairing token.
 * Format: EP1.<roomId>.<keyMaterial>
 */
export function createPairingToken(roomId: string, keyMaterial: string): string {
  const cleanRoom = roomId.trim();
  const cleanKey = keyMaterial.trim();
  if (!cleanRoom || !cleanKey) {
    throw new Error("roomId and keyMaterial must be non-empty");
  }
  return `${PAIRING_TOKEN_PREFIX}.${cleanRoom}.${cleanKey}`;
}

export interface ParsedPairingToken {
  version: typeof PAIRING_TOKEN_PREFIX;
  roomId: string;
  keyMaterial: string;
}

/**
 * Parses and validates a development pairing token string.
 */
export function parsePairingToken(token: string): ParsedPairingToken {
  const trimmed = token.trim();
  const parts = trimmed.split(".");
  if (parts.length !== 3 || parts[0] !== PAIRING_TOKEN_PREFIX) {
    throw new Error("Invalid pairing token format. Expected EP1.<roomId>.<keyMaterial>");
  }
  const [, roomId, keyMaterial] = parts;
  if (!roomId || !keyMaterial) {
    throw new Error("Pairing token is missing roomId or keyMaterial");
  }
  return {
    version: PAIRING_TOKEN_PREFIX,
    roomId,
    keyMaterial,
  };
}

export type InvitationState = "active" | "consumed" | "revoked";
export type PairingRequestState = "pending" | "approved" | "provisioned" | "completed" | "rejected";

/** Phase 4C public approval boundary; this never carries invitation secrets or keys. */
export interface PairingApproval {
  requestId: string; invitationId: string; inviterDeviceId: string;
  inviterSigningFingerprint: string; inviterAgreementFingerprint: string;
  joinerSigningFingerprint: string; joinerAgreementFingerprint: string;
  approvedAt: string; expiresAt: string; version: typeof PROTOCOL_VERSION;
}

/** Ciphertext-only room-key transport persisted by the edge. */
export interface KeyProvisioningEnvelope {
  provisioningId: string; roomId: string; invitationId: string; requestId: string; keyVersion: number;
  senderDeviceId: string; recipientDeviceId: string;
  senderAgreementFingerprint: string; recipientAgreementFingerprint: string;
  encryptedPayload: string; nonce: string; salt: string; context: string; expiresAt: string;
  version: typeof PROTOCOL_VERSION;
}

export interface PairingCompletionChallenge {
  requestId: string; invitationId: string; provisioningId: string; deviceId: string;
  nonce: string; expiresAt: string; version: typeof PROTOCOL_VERSION;
}

export interface PairingCompletionProof {
  challenge: PairingCompletionChallenge; signature: string; signedAt: string;
  version: typeof PROTOCOL_VERSION;
}

/** Public-only material; this type must never acquire private-key fields. */
export interface DevicePublicIdentity {
  deviceId: string;
  signingPublicKeySpki: string;
  signingFingerprint: string;
  agreementPublicKeySpki: string;
  agreementFingerprint: string;
  displayName?: string;
  platform?: Platform;
}

export interface PairingInvitation {
  invitationId: string;
  roomId: string;
  creatorDeviceId: string;
  expiresAt: string;
  state: InvitationState;
}

export interface PairingRequest {
  requestId: string;
  invitationId: string;
  joiningDevice: DevicePublicIdentity;
  createdAt: string;
  expiresAt: string;
  state: PairingRequestState;
}

/** Ciphertext-only export metadata for a Phase 4 Recovery Kit. */
export interface RecoveryKitEnvelope {
  formatVersion: 1;
  kitId: string;
  roomId: string;
  minKeyVersion: number;
  maxKeyVersion: number;
  salt: string;
  nonce: string;
  ciphertext: string;
  exportedAt: string;
}

/** The encrypted Recovery Kit contents. It intentionally has no membership credentials or private keys. */
export interface RecoveryKitPayload {
  schemaVersion: 1;
  roomId: string;
  currentKeyVersion: number;
  keys: Array<{
    keyVersion: number;
    rawKeyMaterial: string;
  }>;
  creatorDeviceId: string;
  exportedAt: string;
}

const BASE64URL_DATA = /^[A-Za-z0-9_-]+$/;
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})$/;

function isBase64UrlData(value: unknown): value is string {
  return typeof value === "string" && BASE64URL_DATA.test(value) && value.length % 4 !== 1;
}

function isPositiveSafeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value > 0;
}

function isIsoTimestamp(value: unknown): value is string {
  return typeof value === "string" && ISO_TIMESTAMP.test(value) && !Number.isNaN(Date.parse(value));
}

export function isRecoveryKitEnvelope(value: unknown): value is RecoveryKitEnvelope {
  if (!value || typeof value !== "object") return false;
  const envelope = value as Record<string, unknown>;
  return (
    envelope.formatVersion === 1 &&
    isNonEmptyString(envelope.kitId) &&
    isNonEmptyString(envelope.roomId) &&
    isPositiveSafeInteger(envelope.minKeyVersion) &&
    isPositiveSafeInteger(envelope.maxKeyVersion) &&
    envelope.minKeyVersion <= envelope.maxKeyVersion &&
    isBase64UrlData(envelope.salt) &&
    isBase64UrlData(envelope.nonce) &&
    isBase64UrlData(envelope.ciphertext) &&
    isIsoTimestamp(envelope.exportedAt)
  );
}

export function isRecoveryKitPayload(value: unknown): value is RecoveryKitPayload {
  if (!value || typeof value !== "object") return false;
  const payload = value as Record<string, unknown>;
  if (
    payload.schemaVersion !== 1 ||
    !isNonEmptyString(payload.roomId) ||
    !isPositiveSafeInteger(payload.currentKeyVersion) ||
    !Array.isArray(payload.keys) ||
    payload.keys.length === 0 ||
    !isNonEmptyString(payload.creatorDeviceId) ||
    !isIsoTimestamp(payload.exportedAt)
  ) return false;

  const keyVersions = new Set<number>();
  for (const key of payload.keys) {
    if (!key || typeof key !== "object") return false;
    const entry = key as Record<string, unknown>;
    if (!isPositiveSafeInteger(entry.keyVersion) || !isBase64UrlData(entry.rawKeyMaterial) || keyVersions.has(entry.keyVersion)) return false;
    keyVersions.add(entry.keyVersion);
  }
  return keyVersions.has(payload.currentKeyVersion);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isIso(value: unknown): value is string { return isNonEmptyString(value) && !Number.isNaN(Date.parse(value)); }
function hasStrings(record: Record<string, unknown>, names: string[]): boolean { return names.every((name) => isNonEmptyString(record[name])); }
function isPhase4cVersion(value: unknown): boolean { return value === PROTOCOL_VERSION; }

export function isValidPairingApproval(value: unknown): value is PairingApproval {
  if (!value || typeof value !== "object") return false; const v = value as Record<string, unknown>;
  return hasStrings(v, ["requestId", "invitationId", "inviterDeviceId", "inviterSigningFingerprint", "inviterAgreementFingerprint", "joinerSigningFingerprint", "joinerAgreementFingerprint"]) && isIso(v.approvedAt) && isIso(v.expiresAt) && isPhase4cVersion(v.version);
}
export function isValidKeyProvisioningEnvelope(value: unknown): value is KeyProvisioningEnvelope {
  if (!value || typeof value !== "object") return false; const v = value as Record<string, unknown>;
  return hasStrings(v, ["provisioningId", "roomId", "invitationId", "requestId", "senderDeviceId", "recipientDeviceId", "senderAgreementFingerprint", "recipientAgreementFingerprint", "encryptedPayload", "nonce", "salt", "context"]) && typeof v.keyVersion === "number" && Number.isInteger(v.keyVersion) && v.keyVersion > 0 && isIso(v.expiresAt) && isPhase4cVersion(v.version);
}
export function isValidPairingCompletionChallenge(value: unknown): value is PairingCompletionChallenge {
  if (!value || typeof value !== "object") return false; const v = value as Record<string, unknown>;
  return hasStrings(v, ["requestId", "invitationId", "provisioningId", "deviceId", "nonce"]) && isIso(v.expiresAt) && isPhase4cVersion(v.version);
}
export function isValidPairingCompletionProof(value: unknown): value is PairingCompletionProof {
  if (!value || typeof value !== "object") return false; const v = value as Record<string, unknown>;
  return isValidPairingCompletionChallenge(v.challenge) && isNonEmptyString(v.signature) && isIso(v.signedAt) && isPhase4cVersion(v.version);
}

/** Structural validation only; server additionally verifies SPKI/fingerprint cryptographically. */
export function isValidDevicePublicIdentity(value: unknown): value is DevicePublicIdentity {
  if (!value || typeof value !== "object") return false;
  const identity = value as Record<string, unknown>;
  return isNonEmptyString(identity.deviceId) && isNonEmptyString(identity.signingPublicKeySpki) &&
    isNonEmptyString(identity.signingFingerprint) && isNonEmptyString(identity.agreementPublicKeySpki) &&
    isNonEmptyString(identity.agreementFingerprint) &&
    (identity.displayName === undefined || isNonEmptyString(identity.displayName)) &&
    (identity.platform === undefined || ["windows", "android", "web", "ios-pwa"].includes(identity.platform as string));
}

export interface ParsedProductionInvitation {
  version: typeof PRODUCTION_INVITATION_PREFIX;
  invitationId: string;
  secret: string;
}

/** EP2 is intentionally distinct from legacy EP1 and contains no room key. */
export function createProductionInvitation(invitationId: string, secret: string): string {
  if (!isNonEmptyString(invitationId) || !/^[A-Za-z0-9_-]{43}$/.test(secret)) {
    throw new Error("Production invitation requires a non-empty id and 256-bit base64url secret");
  }
  return `${PRODUCTION_INVITATION_PREFIX}.${invitationId}.${secret}`;
}

export function parseProductionInvitation(token: string): ParsedProductionInvitation {
  const [version, invitationId, secret, ...extra] = token.trim().split(".");
  if (extra.length || version !== PRODUCTION_INVITATION_PREFIX || !isNonEmptyString(invitationId) || !/^[A-Za-z0-9_-]{43}$/.test(secret || "")) {
    throw new Error("Invalid production invitation format. Expected EP2.<invitationId>.<secret>");
  }
  return { version: PRODUCTION_INVITATION_PREFIX, invitationId, secret: secret! };
}

export interface ClipCreatedEvent {
  type: "clip.created";
  payload: ClipEnvelope;
}

export interface ClipDeletedEvent {
  type: "clip.deleted";
  payload: {
    id: string;
    deletedAt: string;
  };
}

export interface ConnectionReadyEvent {
  type: "connection.ready";
  payload: {
    roomId: string;
    deviceId: string;
  };
}

export interface DeviceRevokedEvent {
  type: "device.revoked";
  payload: {
    deviceId: string;
    revokedAt: string;
  };
}

export interface RoomKeyRotationContext {
  version: 1;
  roomId: string;
  keyVersion: number;
  senderDeviceId: string;
  recipientDeviceId: string;
  senderAgreementFingerprint: string;
  recipientAgreementFingerprint: string;
}

export interface RoomKeyRotationEnvelope {
  roomId: string;
  keyVersion: number;
  senderDeviceId: string;
  recipientDeviceId: string;
  senderAgreementFingerprint: string;
  recipientAgreementFingerprint: string;
  encryptedPayload: string;
  nonce: string;
  salt: string;
  context: string;
  expiresAt: string;
  senderAgreementPublicKeySpki?: string;
}

export interface RoomMemberPublicIdentity {
  deviceId: string;
  agreementPublicKeySpki: string;
  agreementFingerprint: string;
  signingPublicKeySpki?: string;
  signingFingerprint?: string;
  displayName?: string;
  platform?: string;
  keyVersion: number;
}

export interface RoomKeyRotatedEvent {
  type: "room.key_rotated";
  roomId: string;
  keyVersion: number;
  initiatorDeviceId: string;
  rotatedAt: string;
}

export function isRoomKeyRotationContext(value: unknown): value is RoomKeyRotationContext {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    v.version === 1 &&
    isNonEmptyString(v.roomId) &&
    typeof v.keyVersion === "number" &&
    Number.isInteger(v.keyVersion) &&
    v.keyVersion > 0 &&
    isNonEmptyString(v.senderDeviceId) &&
    isNonEmptyString(v.recipientDeviceId) &&
    isNonEmptyString(v.senderAgreementFingerprint) &&
    isNonEmptyString(v.recipientAgreementFingerprint)
  );
}

export function isRoomKeyRotationEnvelope(value: unknown): value is RoomKeyRotationEnvelope {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    isNonEmptyString(v.roomId) &&
    typeof v.keyVersion === "number" &&
    Number.isInteger(v.keyVersion) &&
    v.keyVersion > 0 &&
    isNonEmptyString(v.senderDeviceId) &&
    isNonEmptyString(v.recipientDeviceId) &&
    isNonEmptyString(v.senderAgreementFingerprint) &&
    isNonEmptyString(v.recipientAgreementFingerprint) &&
    isNonEmptyString(v.encryptedPayload) &&
    isNonEmptyString(v.nonce) &&
    isNonEmptyString(v.salt) &&
    isNonEmptyString(v.context) &&
    isIso(v.expiresAt) &&
    (v.senderAgreementPublicKeySpki === undefined || isNonEmptyString(v.senderAgreementPublicKeySpki))
  );
}

export function isRoomMemberPublicIdentity(value: unknown): value is RoomMemberPublicIdentity {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    isNonEmptyString(v.deviceId) &&
    isNonEmptyString(v.agreementPublicKeySpki) &&
    isNonEmptyString(v.agreementFingerprint) &&
    (v.signingPublicKeySpki === undefined || isNonEmptyString(v.signingPublicKeySpki)) &&
    (v.signingFingerprint === undefined || isNonEmptyString(v.signingFingerprint)) &&
    (v.displayName === undefined || isNonEmptyString(v.displayName)) &&
    (v.platform === undefined || isNonEmptyString(v.platform)) &&
    typeof v.keyVersion === "number" &&
    Number.isInteger(v.keyVersion) &&
    v.keyVersion > 0
  );
}

export function isRoomKeyRotatedEvent(value: unknown): value is RoomKeyRotatedEvent {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    v.type === "room.key_rotated" &&
    isNonEmptyString(v.roomId) &&
    typeof v.keyVersion === "number" &&
    Number.isInteger(v.keyVersion) &&
    v.keyVersion > 0 &&
    isNonEmptyString(v.initiatorDeviceId) &&
    isIso(v.rotatedAt)
  );
}

export type RealtimeEvent =
  | ClipCreatedEvent
  | ClipDeletedEvent
  | ConnectionReadyEvent
  | DeviceRevokedEvent
  | RoomKeyRotatedEvent;

export interface ClipsResponse {
  ok: boolean;
  clips: ClipEnvelope[];
}

export interface CreateClipResponse {
  ok: boolean;
  id: string;
}

export interface DeleteClipResponse {
  ok: boolean;
  deleted: boolean;
}

/**
 * Health check response schema for Phase 0 edge endpoint
 */
export interface HealthResponse {
  ok: boolean;
  service: "echopaste";
  protocolVersion: typeof PROTOCOL_VERSION;
}

/**
 * Lightweight helper to determine if input text looks like an absolute HTTP/HTTPS URL
 */
export function detectClipType(text: string): ClipType {
  const trimmed = text.trim();
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
    try {
      new URL(trimmed);
      return "url";
    } catch {
      return "text";
    }
  }
  return "text";
}
